/**
 * AURA Logger
 * Structured logging with console output (local debug) and
 * remote Serilog sink (published/production builds).
 *
 * In production builds, terser strips console.* calls, so only
 * the remote sink persists error data to the Serilog database.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  module: string;
  message: string;
  context?: Record<string, unknown> | undefined;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

class LoggerConfig {
  private static _level: LogLevel = 'info';
  private static _enabled = true;
  private static _remoteUrl: string | null = null;
  private static _appId = 'unknown';
  private static _batch: LogEntry[] = [];
  private static _flushTimer: ReturnType<typeof setTimeout> | null = null;

  static readonly BATCH_SIZE = 10;
  static readonly FLUSH_INTERVAL_MS = 5_000;
  static readonly REMOTE_MIN_LEVEL: LogLevel = 'warn';

  static get level(): LogLevel { return this._level; }
  static set level(v: LogLevel) { this._level = v; }

  static get enabled(): boolean { return this._enabled; }
  static set enabled(v: boolean) { this._enabled = v; }

  static get remoteUrl(): string | null { return this._remoteUrl; }
  static set remoteUrl(v: string | null) { this._remoteUrl = v; }

  static get appId(): string { return this._appId; }
  static set appId(v: string) { this._appId = v; }

  static enqueue(entry: LogEntry): void {
    if (!this._remoteUrl) return;
    if (LOG_LEVELS[entry.level] < LOG_LEVELS[this.REMOTE_MIN_LEVEL]) return;

    this._batch.push(entry);

    if (this._batch.length >= this.BATCH_SIZE) {
      this.flush();
      return;
    }

    if (!this._flushTimer) {
      this._flushTimer = setTimeout(
        () => this.flush(),
        this.FLUSH_INTERVAL_MS
      );
    }
  }

  static flush(): void {
    if (this._flushTimer) {
      clearTimeout(this._flushTimer);
      this._flushTimer = null;
    }
    if (this._batch.length === 0 || !this._remoteUrl) return;

    const payload = {
      appId: this._appId,
      entries: this._batch.splice(0),
    };

    // Fire-and-forget; use sendBeacon for page-unload resilience
    if (typeof navigator?.sendBeacon === 'function') {
      navigator.sendBeacon(
        this._remoteUrl,
        JSON.stringify(payload)
      );
    } else {
      fetch(this._remoteUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {
        /* swallow network errors to avoid recursive logging */
      });
    }
  }
}

export class Logger {
  constructor(private readonly module: string) {}

  private shouldLog(level: LogLevel): boolean {
    if (!LoggerConfig.enabled) return false;
    return LOG_LEVELS[level] >= LOG_LEVELS[LoggerConfig.level];
  }

  private createEntry(
    level: LogLevel,
    message: string,
    context?: Record<string, unknown>
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      module: this.module,
      message,
      context,
    };
  }

  private emit(entry: LogEntry): void {
    const prefix = `[AURA:${entry.module}]`;
    const msg = `${prefix} ${entry.message}`;

    switch (entry.level) {
      case 'debug': console.debug(msg, entry.context ?? ''); break;
      case 'info':  console.info(msg, entry.context ?? '');  break;
      case 'warn':  console.warn(msg, entry.context ?? '');  break;
      case 'error': console.error(msg, entry.context ?? ''); break;
    }

    LoggerConfig.enqueue(entry);
  }

  debug(message: string, context?: Record<string, unknown>): void {
    if (this.shouldLog('debug')) {
      this.emit(this.createEntry('debug', message, context));
    }
  }

  info(message: string, context?: Record<string, unknown>): void {
    if (this.shouldLog('info')) {
      this.emit(this.createEntry('info', message, context));
    }
  }

  warn(message: string, context?: Record<string, unknown>): void {
    if (this.shouldLog('warn')) {
      this.emit(this.createEntry('warn', message, context));
    }
  }

  error(message: string, error?: unknown, context?: Record<string, unknown>): void {
    if (this.shouldLog('error')) {
      const errorContext = {
        ...context,
        error: error instanceof Error ? {
          name: error.name,
          message: error.message,
          stack: error.stack,
        } : error,
      };
      this.emit(this.createEntry('error', message, errorContext));
    }
  }
}

/** Configure log level and enable/disable */
export function configureLogger(options: {
  level?: LogLevel;
  enabled?: boolean;
}): void {
  if (options.level !== undefined) LoggerConfig.level = options.level;
  if (options.enabled !== undefined) LoggerConfig.enabled = options.enabled;
}

/**
 * Enable remote logging to a Serilog-backed API endpoint.
 * Warn and error entries are batched and sent via POST.
 * In production builds console.* calls are stripped by terser,
 * but fetch-based remote logging remains active.
 */
export function configureRemoteLogging(options: {
  url: string;
  appId?: string;
}): void {
  LoggerConfig.remoteUrl = options.url;
  if (options.appId) LoggerConfig.appId = options.appId;

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => LoggerConfig.flush());
  }
}
